import React, { useState } from "react";
import { HelpCircle } from "lucide-react";

/**
 * FieldTooltip — inline help affordance for a workbook question or form field.
 *
 * Restored 2026-09-01: the component was referenced from ClientWorkbook.jsx
 * (and any future client-forms surface) but the file was never committed,
 * which silently broke every Azure Static Web Apps frontend build since
 * PR #1898 (BM-H139e). Deployed SPA bundle was stuck at 29 Aug because
 * "Module not found: '../components/client-forms/FieldTooltip'" aborted
 * every yarn build. PR #1904 (08 Private Client dropdown), #1900, #1903,
 * #1906 all shipped to backend but not frontend as a result.
 *
 * Kept intentionally small and dependency-free: standard-library React,
 * lucide icon already used across the app, no popper/floating-ui, no CSS
 * module. Hover or focus reveals the tooltip; click toggles it for touch.
 *
 * Props:
 *   text    — string, the help text to show (required)
 *   testid  — data-testid for the toggle button (optional)
 *   ariaLabel — accessible label for the trigger (default: "More info")
 */
export default function FieldTooltip({ text, testid, ariaLabel = "More info" }) {
  const [open, setOpen] = useState(false);

  if (!text) return null;

  const wrapperStyle = {
    position: "relative",
    display: "inline-flex",
    marginLeft: 6,
    verticalAlign: "middle",
  };

  const buttonStyle = {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
  };

  const bubbleStyle = {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#0f172a",
    color: "#f8fafc",
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: 400,
    minWidth: 180,
    maxWidth: 280,
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.25)",
    zIndex: 20,
    pointerEvents: "none",
    whiteSpace: "normal",
    textAlign: "left",
  };

  return (
    <span style={wrapperStyle}>
      <button
        type="button"
        data-testid={testid}
        aria-label={ariaLabel}
        aria-expanded={open}
        style={buttonStyle}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <HelpCircle size={14} />
      </button>
      {open && (
        <span role="tooltip" style={bubbleStyle}>
          {text}
        </span>
      )}
    </span>
  );
}
